import { addDays, isSameMonth } from 'date-fns';
import { useTranslation } from 'next-i18next';
import { useState, useEffect, useMemo } from 'react';
import { useKeyNavigation } from '@spa/shared';
import { useDatepicker, START_DATE, END_DATE } from '@datepicker-react/hooks';

import { useDateFormats } from './dateFormats';
import { onDatesChange } from './utils';

const displayFormat = 'dd MMM, yyy';

export const useDateRangeWithoutInputs = (
  ref,
  start = null,
  end = null,
  startName,
  endName,
  onChange,
  availableFrom,
  validation = () => true,
  minBookingDays = 21,
  firstDayOfWeek = 0,
  numberOfMonths = 1,
  isDateBlocked = () => false,
  maxMoveInDate = null
) => {
  const {
    i18n: { language = 'en' }
  } = useTranslation();
  const { format, dateToString, stringToDate } = useDateFormats(language);
  const [initialVisibleMonth, setInitialVisibleMonth] = useState(availableFrom);
  const [focusedInput, setFocusedInput] = useState(null);
  const [startDate, setStartDate] = useState(stringToDate(start));
  const [endDate, setEndDate] = useState(stringToDate(end));
  const { isFocused, blur, focus } = useKeyNavigation(ref);
  const isChoosingStart = !start;
  const isChoosingEnd = !!start && !end;
  const activeStartDate = isChoosingEnd ? startDate : undefined;
  const activeEndDate = isChoosingStart ? endDate : undefined;

  const selectStartDate = (date, input) => {
    if (!validation(date.toISOString(), null)) {
      return null;
    }

    setStartDate(date);
    setFocusedInput(input);
  };

  const selectEndDate = (date, input) => {
    if (!startDate || !validation(startDate.toISOString(), date.toISOString())) {
      return null;
    }

    setEndDate(date);
    setFocusedInput(input);
  };

  const datePicker = useDatepicker({
    minBookingDate: activeStartDate || availableFrom,
    startDate: activeStartDate,
    endDate: activeEndDate,
    focusedInput,
    onDatesChange: onDatesChange(selectStartDate, selectEndDate, isChoosingStart, isChoosingEnd),
    numberOfMonths,
    firstDayOfWeek,
    //the hook has a bug of one day
    minBookingDays: minBookingDays + 1,
    initialVisibleMonth,
    changeActiveMonthOnSelect: false,
    isDateBlocked
  });

  //date conversions input: string -> internalState: Date
  useEffect(() => {
    if (start === dateToString(startDate)) {
      return;
    }

    onChange({ [startName]: dateToString(startDate), [endName]: dateToString(endDate) });
    if (endDate) {
      blur();
    }
  }, [startDate]);

  useEffect(() => {
    if (end === dateToString(endDate)) {
      return;
    }

    onChange({ [startName]: dateToString(startDate), [endName]: dateToString(endDate) });
  }, [endDate]);

  useEffect(() => {
    setStartDate(stringToDate(start));
  }, [start]);

  useEffect(() => {
    setEndDate(stringToDate(end));
  }, [end]);

  //focusChange and blur
  useEffect(() => {
    if (startDate && endDate) {
      return;
    }

    if (!focusedInput) {
      setInitialVisibleMonth(new Date());
      blur();

      return;
    }

    const newActiveMonth = !startDate && !endDate ? availableFrom : addDays(startDate, minBookingDays) || startDate;
    datePicker.goToDate(newActiveMonth);
  }, [focusedInput]);

  useEffect(() => {
    if (!isFocused) {
      setFocusedInput(null);

      return;
    }
  }, [isFocused]);

  const isPresentActiveMonth = useMemo(() => {
    const activeMonth = datePicker.activeMonths[0];
    if (!activeMonth) {
      return false;
    }

    return isSameMonth(activeMonth.date, new Date());
  }, [datePicker.activeMonths[0]]);

  const onResetDates = () => {
    onChange({ [startName]: null, [endName]: null });
    blur();
  };

  useEffect(() => {
    if (!isChoosingStart) {
      return;
    }

    setFocusedInput(START_DATE);
  }, [isChoosingStart]);

  useEffect(() => {
    if (!isChoosingEnd) {
      return;
    }

    setFocusedInput(END_DATE);
  }, [isChoosingEnd]);

  return {
    ...datePicker,
    goToPreviousMonths: isPresentActiveMonth ? () => {} : datePicker.goToPreviousMonths,
    onResetDates,
    isPresentActiveMonth,
    isFocused,
    focus,
    choosingStart: isChoosingStart,
    choosingEnd: isChoosingEnd,
    startDate,
    endDate,
    startDateLabel: format(startDate, displayFormat),
    endDateLabel: format(endDate, displayFormat),
    minBookingDays,
    maxMoveInDate
  };
};
